import { useEffect, useState, type ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

export type ClaimStep = 0 | 1 | 2;

interface Props {
  step: ClaimStep;
}

type IconComponent = ComponentType<SvgIconProps>;

interface Step {
  title: string;
  sub: string;
  Icon: IconComponent;
}

// Step 0 fires for both first-run users (creating an embed wallet) and
// returning users (re-attaching to an existing one), so the copy stays
// neutral — "Initializing wallet" reads correctly either way.
const STEPS: ReadonlyArray<Step> = [
  { title: 'Initializing wallet', sub: 'Approve the connect prompt in your wallet.', Icon: AccountBalanceWalletIcon },
  { title: 'Preparing claim', sub: 'Signing your delegation token.', Icon: KeyIcon },
  { title: 'Creating security vault', sub: 'Provisioning your encrypted storage.', Icon: SafetyCheckIcon },
] as const;

const FALLBACK_STEP: Step = {
  title: 'Initializing wallet',
  sub: 'Approve the connect prompt in your wallet.',
  Icon: AccountBalanceWalletIcon,
};

export const VAULT_QUOTES: readonly string[] = [
  'Building your private vault…',
  'This vault belongs to you.',
  'Only you decide who sees inside.',
  'Agents see only what you allow.',
  'Cut off any agent, anytime.',
  'Your data is never sold.',
  'Take it with you wherever you want.',
];

const QUOTE_INTERVAL_MS = 2200;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function VaultClaiming({ step }: Props) {
  const cur = STEPS[step] ?? FALLBACK_STEP;
  const Icon = cur.Icon;
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const timer = setInterval(
      () => setQuoteIdx((i) => (i + 1) % VAULT_QUOTES.length),
      QUOTE_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="vault-claim">
      <div className="vault-claim__halo" aria-hidden />
      <div className="vault-claim__card">
        <div className="vault-claim__dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`vault-claim__dot ${i <= step ? 'is-on' : ''} ${i === step ? 'is-active' : ''}`} />
          ))}
        </div>
        <div className="vault-claim__icon">
          <Icon sx={{ fontSize: 48, color: 'currentColor' }} />
          <span className="vault-claim__pulse" aria-hidden />
        </div>
        <div className="vault-claim__title">{cur.title}</div>
        <div className="vault-claim__sub">{cur.sub}</div>
        <div className="vault-claim__quote" key={quoteIdx}>{VAULT_QUOTES[quoteIdx] ?? ''}</div>
      </div>
    </div>
  );
}
